import { Unit } from './types';

export const HANGUL: Unit = {
  id: 'hangul',
  number: 1,
  title: 'Hangul',
  description: 'The Korean alphabet. Learn the letters, build syllables and read your first words.',
  emoji: '한',
  lessons: [
    {
      id: 'vowels',
      title: 'Vowels',
      summary: 'The six basic vowel letters',
      exercises: [
        { kind: 'teach', ko: 'ㅏ', speak: '아', roman: 'a', en: 'Sounds like the "a" in "father"', note: 'A vowel is never written alone. The circle ㅇ goes in front of it and makes no sound, so ㅏ is written 아.' },
        { kind: 'teach', ko: 'ㅓ', speak: '어', roman: 'eo', en: 'Sounds like the "u" in "fun"' },
        { kind: 'teach', ko: 'ㅗ', speak: '오', roman: 'o', en: 'Sounds like the "o" in "go"' },
        { kind: 'teach', ko: 'ㅜ', speak: '우', roman: 'u', en: 'Sounds like the "oo" in "moon"' },
        { kind: 'teach', ko: 'ㅡ', speak: '으', roman: 'eu', en: 'Like "oo" but with your lips flat, not round' },
        { kind: 'teach', ko: 'ㅣ', speak: '이', roman: 'i', en: 'Sounds like the "ee" in "see"' },
        { kind: 'choice', prompt: 'Which sound does this letter make?', ko: 'ㅓ', options: ['eo', 'o', 'u', 'a'], answer: 0 },
        { kind: 'match', prompt: 'Match each vowel to its sound', pairs: [['ㅏ', 'a'], ['ㅗ', 'o'], ['ㅜ', 'u'], ['ㅣ', 'i']] },
        { kind: 'choice', prompt: 'Listen. Which vowel did you hear?', speak: '우', listenOnly: true, options: ['ㅏ', 'ㅜ', 'ㅣ', 'ㅓ'], answer: 1, explain: '우 is ㅜ, "u".' },
        { kind: 'choice', prompt: 'Which sound does this letter make?', ko: 'ㅡ', options: ['i', 'eu', 'eo', 'o'], answer: 1 },
      ],
    },
    {
      id: 'consonants',
      title: 'Consonants',
      summary: 'Ten common consonant letters',
      exercises: [
        { kind: 'teach', ko: 'ㄱ', speak: '가', roman: 'g / k', en: 'Between "g" and "k". With ㅏ it is 가, "ga".' },
        { kind: 'teach', ko: 'ㄴ', speak: '나', roman: 'n', en: 'Like "n". 나, "na".' },
        { kind: 'teach', ko: 'ㄷ', speak: '다', roman: 'd / t', en: 'Between "d" and "t". 다, "da".' },
        { kind: 'teach', ko: 'ㄹ', speak: '라', roman: 'r / l', en: 'Between "r" and "l". 라, "ra".' },
        { kind: 'teach', ko: 'ㅁ', speak: '마', roman: 'm', en: 'Like "m". 마, "ma".' },
        { kind: 'match', prompt: 'Match each consonant to its sound', pairs: [['ㄱ', 'g'], ['ㄴ', 'n'], ['ㅁ', 'm'], ['ㄹ', 'r / l']] },
        { kind: 'teach', ko: 'ㅂ', speak: '바', roman: 'b / p', en: 'Between "b" and "p". 바, "ba".' },
        { kind: 'teach', ko: 'ㅅ', speak: '사', roman: 's', en: 'Like "s". 사, "sa".' },
        { kind: 'teach', ko: 'ㅇ', speak: '아', roman: '(silent) / ng', en: 'Silent at the start of a syllable. At the bottom it sounds "ng".' },
        { kind: 'teach', ko: 'ㅈ', speak: '자', roman: 'j', en: 'Like "j". 자, "ja".' },
        { kind: 'teach', ko: 'ㅎ', speak: '하', roman: 'h', en: 'Like "h". 하, "ha".' },
        { kind: 'choice', prompt: 'Which sound does this letter make?', ko: 'ㅂ', options: ['m', 'b / p', 'd / t', 's'], answer: 1 },
        { kind: 'choice', prompt: 'Listen. Which letter starts the sound?', speak: '하', listenOnly: true, options: ['ㅎ', 'ㅅ', 'ㅈ', 'ㄴ'], answer: 0, explain: 'You heard 하, "ha".' },
        { kind: 'match', prompt: 'Match each consonant to its sound', pairs: [['ㅂ', 'b / p'], ['ㅅ', 's'], ['ㅈ', 'j'], ['ㅎ', 'h']] },
      ],
    },
    {
      id: 'syllables',
      title: 'Syllables',
      summary: 'Put letters together into blocks',
      exercises: [
        { kind: 'teach', ko: 'ㄱ + ㅏ = 가', speak: '가', roman: 'g + a = ga', en: 'Korean letters are written in blocks. Each block is one syllable: a consonant, then a vowel.' },
        { kind: 'teach', ko: 'ㄴ + ㅗ = 노', speak: '노', roman: 'n + o = no', en: 'Tall vowels (ㅏ ㅓ ㅣ) go to the right of the consonant. Flat ones (ㅗ ㅜ ㅡ) go underneath.' },
        { kind: 'teach', ko: 'ㅂ + ㅏ + ㅂ = 밥', speak: '밥', roman: 'b + a + p = bap', en: 'A third letter can sit at the bottom and closes the syllable. This one is 밥, "rice" or "meal".' },
        { kind: 'choice', prompt: 'ㅁ + ㅏ makes which block?', options: ['마', '모', '나', '바'], answer: 0 },
        { kind: 'choice', prompt: 'ㄷ + ㅜ makes which block?', options: ['도', '두', '누', '드'], answer: 1 },
        { kind: 'choice', prompt: 'Which block says "no"?', options: ['나', '누', '노', '너'], answer: 2 },
        { kind: 'choice', prompt: 'Listen. Which block did you hear?', speak: '바', listenOnly: true, options: ['마', '바', '파', '다'], answer: 1 },
        { kind: 'match', prompt: 'Match each block to its sound', pairs: [['가', 'ga'], ['노', 'no'], ['수', 'su'], ['히', 'hi']] },
      ],
    },
    {
      id: 'reading',
      title: 'Reading',
      summary: 'Read simple two-syllable words',
      exercises: [
        { kind: 'teach', ko: '우유', roman: 'u-yu', en: 'milk', picture: '🥛', note: 'ㅠ is ㅜ with an extra line: "yu".' },
        { kind: 'teach', ko: '아이', roman: 'a-i', en: 'child', picture: '🧒' },
        { kind: 'teach', ko: '나무', roman: 'na-mu', en: 'tree', picture: '🌳' },
        { kind: 'teach', ko: '모자', roman: 'mo-ja', en: 'hat', picture: '🧢' },
        { kind: 'teach', ko: '바다', roman: 'ba-da', en: 'sea', picture: '🌊' },
        { kind: 'choice', prompt: 'How do you read this word?', ko: '나무', options: ['namu', 'mamu', 'nabu', 'namo'], answer: 0 },
        { kind: 'choice', prompt: 'How do you read this word?', ko: '모자', options: ['moja', 'boja', 'mosa', 'meoja'], answer: 0 },
        { kind: 'choice', prompt: 'Which picture is 우유?', ko: '우유', options: ['🥛', '🌳', '🧢', '🌊'], pictureOptions: true, answer: 0 },
        { kind: 'match', prompt: 'Match the word to its meaning', pairs: [['아이', 'child'], ['나무', 'tree'], ['바다', 'sea'], ['모자', 'hat']] },
      ],
    },
    {
      id: 'first-words',
      title: 'First words',
      summary: 'Water, rice, yes, no and thank you',
      exercises: [
        { kind: 'teach', ko: '물', roman: 'mul', en: 'water', picture: '💧' },
        { kind: 'teach', ko: '밥', roman: 'bap', en: 'rice, a meal', picture: '🍚' },
        { kind: 'teach', ko: '집', roman: 'jip', en: 'house, home', picture: '🏠' },
        { kind: 'teach', ko: '네', roman: 'ne', en: 'yes' },
        { kind: 'teach', ko: '아니요', roman: 'a-ni-yo', en: 'no' },
        { kind: 'teach', ko: '감사합니다', roman: 'gam-sa-ham-ni-da', en: 'thank you', note: 'You will hear this every day at work in Korea.' },
        { kind: 'choice', prompt: 'Listen. Which picture matches?', speak: '물', listenOnly: true, options: ['💧', '🍚', '🏠', '🌳'], pictureOptions: true, answer: 0, explain: '물 (mul) is water.' },
        { kind: 'choice', prompt: 'What does this mean?', ko: '네', options: ['yes', 'no', 'thank you', 'water'], answer: 0 },
        { kind: 'choice', prompt: 'Your manager helps you. What do you say?', options: ['감사합니다', '아니요', '밥', '집'], answer: 0, explain: '감사합니다 means "thank you".' },
        { kind: 'match', prompt: 'Match the word to its meaning', pairs: [['물', 'water'], ['밥', 'rice'], ['집', 'home'], ['아니요', 'no']] },
      ],
    },
  ],
};
